"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Icon = void 0;
exports.withIcon = withIcon;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _constants = require("../../constants");
var _IconMixin = require("../../mixins/components/IconMixin");
var _customPropTypes = _interopRequireDefault(require("../../util/custom-prop-types"));
var _identifier = require("../../util/identifier");
var _excluded = ["children", "fontColor", "mediaQuery", "margin", "marginX", "marginY", "marginLeft", "marginRight", "marginTop", "marginBottom", "padding", "paddingX", "paddingY", "paddingLeft", "paddingRight", "paddingTop", "paddingBottom"],
  _excluded2 = ["children", "title"];
/**
 * Icon is a generic wrapper used to provide icon functionality and styling.
 * Unless you are creating your own icons, you will generally want to use one of the
 * individual icon components.
 *
 * @since 6.4.0
 */
var Icon = /*#__PURE__*/(0, _styledComponents.default)(function (_ref) {
  var children = _ref.children,
    fontColor = _ref.fontColor,
    mediaQuery = _ref.mediaQuery,
    margin = _ref.margin,
    marginX = _ref.marginX,
    marginY = _ref.marginY,
    marginLeft = _ref.marginLeft,
    marginRight = _ref.marginRight,
    marginTop = _ref.marginTop,
    marginBottom = _ref.marginBottom,
    padding = _ref.padding,
    paddingX = _ref.paddingX,
    paddingY = _ref.paddingY,
    paddingLeft = _ref.paddingLeft,
    paddingRight = _ref.paddingRight,
    paddingTop = _ref.paddingTop,
    paddingBottom = _ref.paddingBottom,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  return /*#__PURE__*/(0, _react.cloneElement)(_react.Children.only(children), rest);
}).withConfig({
  displayName: "Icon",
  componentId: "c11n-8-107-0__sc-13llmml-0"
})(["", ""], _IconMixin.IconMixin);
exports.Icon = Icon;
Icon.displayName = 'Icon';
Icon.propTypes = {
  /**
   * For presentational icons, this should be set to `true`.
   * For contextually meaningful icons, this should be set to `false`
   * and a `<title>` and `<desc>` should be added.
   *
   * @deprecated Use the `title` prop instead
   */
  'aria-hidden': _propTypes.default.oneOf(['true', 'false']),
  /**
   * If `aria-hidden="false"`, you need to add a `<title>` and `<desc>` and point to them with the
   * `aria-describedby` attribute to make the icon accessible to screen readers.
   *
   * @deprecated Use the `title` prop instead
   */
  'aria-describedby': _propTypes.default.string,
  /**
   * The SVG [`focusable`](https://www.w3.org/TR/SVGTiny12/interact.html#focusable-attr)
   * attribute.
   */
  'focusable': _propTypes.default.oneOf(['true', 'false', 'auto']),
  /**
   * Change the icon color with a color token, e.g. `'textDark'`, `'textWhite'`.
   */
  'fontColor': _propTypes.default.string,
  /**
   * The role is set to "img" by default to exclude all child content from the accessibility tree.
   */
  'role': _propTypes.default.string,
  /**
   * By default, icons are sized to `1em` to match the size of the text content.
   * For fixed-width sizes, you can use the `size` prop.
   *
   * Supports inline media query objects.
   */
  'size': _customPropTypes.default.inlineMediaQuery(_propTypes.default.oneOf(Object.values(_constants.ICON_SIZES))),
  /**
   * Creates an accessible label for the icon for contextually meaninful icons, and sets
   * the appropriate `aria` attributes. Icons are hidden from screen readers by default
   * without this prop.
   *
   * Note: specifying `aria-labelledby`, `aria-hidden`, or `children` manually while using
   * this prop may produce accessibility errors. This prop is only available on prebuilt icons
   * within Constellation.
   */
  'title': _propTypes.default.string,
  /**
   * @deprecated Use inline media query objects
   */
  'mediaQuery': _customPropTypes.default.mediaQuery
};
Icon.defaultProps = {
  'aria-hidden': 'true',
  'focusable': 'false',
  'role': 'img'
};
var generateDisplayName = function generateDisplayName(target) {
  var displayName;
  if (target.displayName) {
    // eslint-disable-next-line prefer-destructuring
    displayName = target.displayName;
  } else if (target.name) {
    // make various icon export patterns yield consistent output
    displayName = target.name.replace(/^(?:Icon)?(.*?)(?:SVG)?$/i, 'Icon$1');
  }
  return displayName;
};
function withIcon(Component) {
  var IconComponent = function IconComponent(_ref2) {
    var children = _ref2.children,
      title = _ref2.title,
      props = (0, _objectWithoutProperties2.default)(_ref2, _excluded2);
    var id = (0, _react.useRef)((0, _identifier.identifier)());
    var iconChildren = (0, _react.useMemo)(function () {
      if (children) {
        return children;
      }
      return title ? /*#__PURE__*/_react.default.createElement("title", {
        id: id.current
      }, title) : null;
    }, [children, title, id]);
    return /*#__PURE__*/_react.default.createElement(Icon, (0, _extends2.default)({
      "aria-hidden": title ? 'false' : 'true',
      "aria-labelledby": title ? id.current : undefined
    }, props), /*#__PURE__*/_react.default.createElement(Component, null, iconChildren));
  };
  IconComponent.propTypes = process.env.NODE_ENV !== "production" ? {
    children: _propTypes.default.node
  } : {};
  IconComponent.displayName = generateDisplayName(Component);
  return IconComponent;
}

/** @component */